import type { RealEstateContact } from "../../@types/sharedSchema";

/**
 * ONE STORE FOR "THE DOCUMENT OF THIS CONTACT", SHARED BY EVERY SURFACE ON THE SCREEN.
 *
 * The document-granularity twin of `contactLookupStore`. That store answers "who is this
 * phone" with a thirty-field projection for the rows of a list; this one holds the WHOLE
 * document for the card, the panel and the dialog that show one contact — read through
 * `useContactDocument` / `useContactByPhone` / `useLiveContact`, and never directly.
 *
 * ── What it replaces ────────────────────────────────────────────────────────────────
 *
 * A Firestore listener per open card on `dashboard-users-v2/{email}/contacts/{id}`. The
 * contact record is moving to Postgres (the server's contacts-postgres-authority plan, D2),
 * where there is no document to listen to, so the reconciler becomes this store plus a
 * listener on the contact's tiny SIGNAL document: the server bumps `crm_contact.version` on
 * every write and mirrors it into `contact-signals/{id}` as `{ v, at }`; a reader that holds
 * a lower version fetches `POST /contacts/get` again. See `contactDocumentSource.ts` for
 * which of the two mechanisms answers today.
 *
 * ── Three states ────────────────────────────────────────────────────────────────────
 *
 *   resolving   asked, no answer yet (or the ask FAILED — `error` says so)   → claim nothing
 *   absent      answered: there is no such document in that book              → may offer "add"
 *   present     the document, with the mirror version it was read at
 *
 * `undefined` from `get` is "nobody asked", which the hooks treat as resolving-and-must-ask.
 * As in the lookup store, a request that fails NEVER records `absent`: "not in your book"
 * is a statement screens act on, and a network blip is not evidence for it. The failure is
 * kept on the entry beside whatever answer it already held.
 *
 * ── Keyed by BOOK, then by contact ──────────────────────────────────────────────────
 *
 * A book is the owner key a document lives under — the signed-in email for a personal book
 * (every caller today), `org:<id>` for a pooled agency book once those exist. The same
 * phone can be a different person in two books, so the key is the pair.
 *
 * The key is folded HERE, once (`normalizeBookKey`), and nowhere else. Readers key by the
 * signed-in email as Firebase hands it over; the write-through keys by the `ownerUserId`
 * the server answered with, which its `normalizeCrmUserId` has already lowercased. Folded
 * at each caller instead, an account with a capital letter in its address wrote the
 * response's document into one book and read another — the open panel never repainted,
 * with nothing failing. Nothing reads a key back out of the store, so the folded spelling
 * is never seen outside it.
 *
 * ── Freshness: signals first, the clock as the fallback ─────────────────────────────
 *
 * A held answer is shown for as long as it is held. It is re-asked when its contact's
 * signal says a newer version exists (`signalV`, below), and — because a signal can be
 * missed (the listener was in error, the tab was asleep, the signal document is not
 * written yet for an owner the outbox has not reached) — also once it is older than
 * {@link DOCUMENT_STALE_AFTER_MS} and a reader asks again. The stale value keeps rendering
 * while the new one is fetched.
 *
 * Writes made in THIS browser do not wait for either: `useContactWrites` writes the
 * response's document through (`put`), marks a delete `absent`, names the ids an import
 * wrote to `invalidate`, and invalidates everything it cannot enumerate (a bulk edit, a
 * delete the server resolved from a filter), so an open panel repaints in the response tick.
 *
 * ── It belongs to ONE account ───────────────────────────────────────────────────────
 *
 * `bindAccount` drops everything when the signed-in user changes, for the same reason the
 * lookup store does: nothing is cleared "on logout", so the store is stamped with its
 * account and an answer for the previous account that lands late is discarded by `session`.
 *
 * Pure: no React, no network, an injected clock. `createContactDocumentStore` exists for
 * the tests; the app uses the one instance exported at the bottom.
 */

/** How long an answer is trusted without a signal before the next reader re-asks. */
export const DOCUMENT_STALE_AFTER_MS = 60 * 1000;

/** A pooled agency's book key is `org:<orgId>` (the plan's D4); every other key is a personal book's email. */
export const ORG_BOOK_KEY_PREFIX = "org:";

/**
 * The one spelling a book is filed under. A personal book's key is its owner's user id,
 * folded as the server folds one (`normalizeCrmUserId`: trim, then lowercase). An `org:` key
 * is only trimmed: Firestore ids are case-sensitive, and the server never folds them.
 */
export const normalizeBookKey = (bookKey: string): string => {
  const trimmed = bookKey.trim();
  return trimmed.startsWith(ORG_BOOK_KEY_PREFIX) ? trimmed : trimmed.toLowerCase();
};

export type ContactDocumentState = "resolving" | "absent" | "present";

/** What prompted a fetch — a label on the shadow-mode latency meter, nothing more. */
export type ContactFetchTrigger = "mount" | "stale" | "signal" | "visibility" | "poll" | "candidates";

export type ContactDocumentEntry = {
  state: ContactDocumentState;
  /** Set only when `present`. The SAME object until the document is replaced, so effects can depend on it. */
  doc?: RealEstateContact;
  /** `crm_contact.version` the answer was read at; 0 while unknown (no row, or the mirror has no column yet). */
  version: number;
  /** When the answer landed (ms). 0 while no answer has. */
  fetchedAt: number;
  /** The last FAILED ask for this entry, kept beside whatever answer is held. Cleared by the next answer. */
  error?: Error;
  /**
   * A request is out for this entry right now — a fetch, or a WRITE from this browser that
   * claimed the entry before its request left (`useContactWrites`). Readers keep showing
   * what is held, and a signal that arrives meanwhile waits for the answer.
   */
  inFlight: boolean;
  /**
   * The signal `v` the current answer was fetched for, or the delete signal that marked it
   * absent; 0 when no signal prompted it (a mount, a write-through). Compared with `version`
   * by `signalVerdict`. NOT a high-water mark: the server starts a re-created contact again
   * at version 1, so a delete's `v` kept as a ceiling would swallow every signal of the
   * contact's next life — see `signalVerdict`.
   */
  signalV: number;
  /** How the last answer was prompted and how long it took (shadow-mode meter). */
  lastFetch?: { trigger: ContactFetchTrigger; latencyMs: number };
  /**
   * `absent` as READ UNDER `mine` — a fetch or a phone check that found nothing in the
   * viewer's own scope. On a pooled book both scopes file under the office's key, and `mine`
   * is narrowed to the viewer's own list there, so this absence means "not on my list", never
   * "the office has nobody by that number": it answers no reader asking under `org`
   * (`entryAnswersScope`). Unset on an absence that is a fact for every scope — a delete, a
   * delete signal — and on every `present`.
   */
  absentUnderMine?: true;
};

export type ContactDocumentAnswer = { doc: RealEstateContact; version: number };

export type ContactDocumentFetchMeta = {
  trigger: ContactFetchTrigger;
  /** From the prompt (the signal's `at`, or the request leaving) to the answer landing. */
  latencyMs: number;
  /** The signal `v` the fetch was made for, when it was made for one. */
  signalV?: number;
  /**
   * The scope the request was made under (`ContactReadScope`); absent = `mine`, which is
   * every phone check's and every own-book reader's. Kept on an `absent` answer only — see
   * `ContactDocumentEntry.absentUnderMine`.
   */
  scope?: "mine" | "org";
};

export type ContactDocumentStore = {
  /** Idempotent. A CHANGE of account drops every entry and starts a new session. */
  bindAccount: (account: string | null) => void;
  /** Identifies the bound account's lifetime. Capture it when asking; pass it back with the answer. */
  session: () => number;
  /** Moves when {@link ContactDocumentStore.invalidate} runs — mounted readers re-check what they hold. */
  epoch: () => number;
  get: (bookKey: string, contactId: string) => ContactDocumentEntry | undefined;
  /**
   * "I am about to ask for this document." Marks the entry in flight — creating a
   * `resolving` one if nobody asked before — and returns whether the caller should send
   * the request: false means a request is already out, and its answer serves everyone.
   */
  claim: (bookKey: string, contactId: string) => boolean;
  /** The same for several ids at once (the spellings of one phone). Returns the ids claimed. */
  claimMany: (bookKey: string, contactIds: readonly string[]) => string[];
  /**
   * The answer to a claim: a document, or `null` for "no such document". Skipped when the
   * id is no longer in flight — a write-through landed while the request was out, and it
   * is newer than the answer — or when the session has changed.
   */
  record: (
    session: number,
    bookKey: string,
    contactId: string,
    answer: ContactDocumentAnswer | null,
    meta: ContactDocumentFetchMeta
  ) => void;
  /**
   * The answer to a CANDIDATES claim (`POST /contacts/get` with every spelling of a phone,
   * in order of preference; the server returns the first that exists). The found id is
   * `present`, every candidate BEFORE it is `absent` (the server checked them in order),
   * and every candidate after it is unknown again — it was never checked. `null` settles
   * every candidate `absent`. As with `record`, an id is settled only while it is in flight
   * — whichever answer lands first for an id a by-id reader is also asking about settles
   * it, and the later one is skipped.
   */
  recordCandidates: (
    session: number,
    bookKey: string,
    candidates: readonly string[],
    found: (ContactDocumentAnswer & { contactId: string }) | null,
    meta: ContactDocumentFetchMeta
  ) => void;
  /**
   * A claim that FAILED: the held answer stays, the error is noted, readers are told. With no
   * `error`, a claim WITHDRAWN — a write that held the entry and then sent nothing, or whose
   * request did not land: nothing is noted (a failed write is not a failed read), readers
   * re-evaluate, and a signal that arrived meanwhile is acted on.
   */
  release: (session: number, bookKey: string, contactIds: readonly string[], error?: Error) => void;
  /** Write-through of a document the server just returned (a create, an update). */
  put: (bookKey: string, contactId: string, doc: RealEstateContact, version: number) => void;
  /** Write-through of a delete, or of a signal that says the contact is gone (`signalV`). */
  markAbsent: (bookKey: string, contactIds: readonly string[], signalV?: number) => void;
  /**
   * What is held is now suspect: everything (no arguments — a bulk edit, a delete the server
   * resolved from a filter), one book, or named ids (the rows an import wrote, a re-key's
   * new number). Values keep rendering. Every mounted reader is woken (the epoch moves), but
   * only one whose entry was named — or had gone stale on the clock anyway — re-asks:
   * `shouldFetch` reads the entry, not the epoch.
   */
  invalidate: (bookKey?: string, contactIds?: readonly string[]) => void;
  subscribe: (listener: () => void) => () => void;
};

export const createContactDocumentStore = (now: () => number = Date.now): ContactDocumentStore => {
  let boundAccount: string | null = null;
  let currentSession = 0;
  let currentEpoch = 0;
  const books = new Map<string, Map<string, ContactDocumentEntry>>();
  const subscribers = new Set<() => void>();

  const notify = (): void => subscribers.forEach((listener) => listener());

  // Every way in folds the key here or in `get` / `invalidate` below — the one boundary.
  const bookOf = (bookKey: string): Map<string, ContactDocumentEntry> => {
    const key = normalizeBookKey(bookKey);
    let book = books.get(key);
    if (!book) {
      book = new Map();
      books.set(key, book);
    }
    return book;
  };

  const RESOLVING: ContactDocumentEntry = { state: "resolving", version: 0, fetchedAt: 0, inFlight: false, signalV: 0 };

  /** A new entry object every time: readers compare entries by identity. */
  const set = (bookKey: string, contactId: string, entry: ContactDocumentEntry): void => {
    bookOf(bookKey).set(contactId, entry);
  };

  const settle = (
    bookKey: string,
    contactId: string,
    answer: ContactDocumentAnswer | null,
    meta?: ContactDocumentFetchMeta,
    signalV?: number
  ): void => {
    // The signal this answer was fetched for, or none — never the max with what was held:
    // an answer is a fresh reading of the contact, and carrying an older delete's `v`
    // forward as a ceiling is exactly what hid a re-created contact (its versions restart at 1).
    set(bookKey, contactId, {
      state: answer ? "present" : "absent",
      ...(answer ? { doc: answer.doc } : {}),
      version: answer?.version ?? 0,
      fetchedAt: now(),
      inFlight: false,
      signalV: meta?.signalV ?? signalV ?? 0,
      ...(meta ? { lastFetch: { trigger: meta.trigger, latencyMs: meta.latencyMs } } : {}),
      // Only a READ can be narrow: `markAbsent` (a delete, a delete signal) carries no meta.
      ...(!answer && meta && meta.scope !== "org" ? { absentUnderMine: true as const } : {})
    });
  };

  const claimOne = (bookKey: string, contactId: string): boolean => {
    if (!contactId) return false;
    const held = bookOf(bookKey).get(contactId);
    if (held?.inFlight) return false;
    set(bookKey, contactId, { ...(held ?? RESOLVING), inFlight: true });
    return true;
  };

  return {
    bindAccount: (account) => {
      const next = (account && normalizeBookKey(account)) || null;
      if (next === boundAccount) return;
      boundAccount = next;
      currentSession += 1;
      books.clear();
      // No `notify()`: this runs while a reader is rendering, and every reader re-renders on
      // the same auth change that brought it here.
    },

    session: () => currentSession,
    epoch: () => currentEpoch,

    get: (bookKey, contactId) => books.get(normalizeBookKey(bookKey))?.get(contactId),

    claim: claimOne,

    claimMany: (bookKey, contactIds) => contactIds.filter((contactId) => claimOne(bookKey, contactId)),

    record: (session, bookKey, contactId, answer, meta) => {
      if (session !== currentSession) return;
      if (!bookOf(bookKey).get(contactId)?.inFlight) return;
      settle(bookKey, contactId, answer, meta);
      notify();
    },

    recordCandidates: (session, bookKey, candidates, found, meta) => {
      if (session !== currentSession) return;
      const book = bookOf(bookKey);
      let changed = false;
      let passedFound = false;
      for (const contactId of candidates) {
        if (!book.get(contactId)?.inFlight) continue;
        if (found && contactId === found.contactId) {
          settle(bookKey, contactId, { doc: found.doc, version: found.version }, meta);
          passedFound = true;
        } else if (found && passedFound) {
          // Not checked by the server: back to "nobody asked", so a reader for this id asks.
          book.delete(contactId);
        } else {
          settle(bookKey, contactId, null, meta);
        }
        changed = true;
      }
      if (changed) notify();
    },

    release: (session, bookKey, contactIds, error) => {
      if (session !== currentSession) return;
      const book = bookOf(bookKey);
      let released = false;
      for (const contactId of contactIds) {
        const held = book.get(contactId);
        if (!held?.inFlight) continue;
        set(bookKey, contactId, error ? { ...held, inFlight: false, error } : { ...held, inFlight: false });
        released = true;
      }
      if (released) notify();
    },

    put: (bookKey, contactId, doc, version) => {
      settle(bookKey, contactId, { doc, version });
      notify();
    },

    markAbsent: (bookKey, contactIds, signalV) => {
      if (contactIds.length === 0) return;
      for (const contactId of contactIds) settle(bookKey, contactId, null, undefined, signalV);
      notify();
    },

    invalidate: (bookKey, contactIds) => {
      const targets = bookKey ? [books.get(normalizeBookKey(bookKey))].filter(Boolean) : Array.from(books.values());
      for (const book of targets) {
        const ids = contactIds ?? Array.from(book!.keys());
        for (const contactId of ids) {
          const held = book!.get(contactId);
          if (held) book!.set(contactId, { ...held, fetchedAt: 0 });
        }
      }
      currentEpoch += 1;
      notify();
    },

    subscribe: (listener) => {
      subscribers.add(listener);
      return () => {
        subscribers.delete(listener);
      };
    }
  };
};

/** The app's one instance. */
export const contactDocumentStore = createContactDocumentStore();
